/**
 * VersionGate — consumes `useVersionGate()` and surfaces either a
 * non-dismissable force-update modal or a dismissable soft-update banner.
 *
 * See docs/pwa-update-and-offline.md §1.5.
 *
 * Default semantics: when `force_update_message` and `soft_update_message`
 * are both null in /runtime-config.json (today's steady state) this
 * component renders nothing — it is a no-op until an operator opts in.
 *
 * NOTE on analytics: the spec calls for `ariadne.version_gate.shown.v1`
 * via `useTrack()`. We intentionally do NOT emit here in this PR because
 * VersionGate lives as a SIBLING of <RouterProvider> in main.tsx (so the
 * modal portal can render above any route), which puts it outside the
 * TanStack Router context. `useTrack` reads `useRouterState`, which
 * panics with "No router context found" when called from a sibling.
 * Lighthouse caught this — the SPA failed to paint at /login because
 * the gate threw on first render. Analytics for the gate will land in a
 * follow-up once we either move the emit into a router-scoped child or
 * replace useTrack with a router-agnostic envelope builder.
 */
import { useState } from 'react'
import {
  Button,
  DialogContent,
  DialogDescription,
  DialogRoot,
  DialogTitle,
  Stack,
} from '@francisquiniai/hestia-web'
import { useVersionGate } from '@/lib/version-gate/useVersionGate'

const SOFT_DISMISS_PREFIX = 'cuidari.version_gate.soft_dismissed.'

function softDismissKey(targetVersion: string): string {
  return `${SOFT_DISMISS_PREFIX}${targetVersion}`
}

function isSoftDismissedFor(targetVersion: string): boolean {
  try {
    return window.sessionStorage.getItem(softDismissKey(targetVersion)) === '1'
  } catch {
    return false
  }
}

function rememberSoftDismiss(targetVersion: string): void {
  try {
    window.sessionStorage.setItem(softDismissKey(targetVersion), '1')
  } catch {
    // sessionStorage not available (private mode, etc) — banner re-appears
    // on next mount; acceptable degradation.
  }
}

export function VersionGate() {
  const { status, reloadForUpdate } = useVersionGate()

  // Local re-render trigger for the "Depois" dismiss action. We persist
  // the dismissal in sessionStorage (keyed by target_version) and read it
  // here on every render; bumping this state forces the read.
  const [dismissTick, setDismissTick] = useState(0)

  // Dismiss bookkeeping for soft banner: re-derived from sessionStorage so
  // a refresh re-shows the banner only if a different target_version came
  // online while the user was off-tab.
  const softDismissed =
    status.kind === 'soft_update' &&
    // dismissTick read so React re-evaluates after `setDismissTick(...)`.
    dismissTick >= 0 &&
    isSoftDismissedFor(status.target_version)

  if (status.kind === 'current') return null

  if (status.kind === 'force_update') {
    return (
      <DialogRoot open onOpenChange={() => undefined}>
        <DialogContent
          data-testid="version-gate-force"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogTitle>Atualização necessária</DialogTitle>
          <DialogDescription>{status.message}</DialogDescription>
          <Stack gap="3" className="mt-4">
            <Button
              data-testid="version-gate-force-reload"
              onClick={reloadForUpdate}
            >
              Atualizar agora
            </Button>
          </Stack>
        </DialogContent>
      </DialogRoot>
    )
  }

  // soft_update
  if (softDismissed) return null

  return (
    <div
      data-testid="version-gate-soft"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        background: 'var(--color-primary)',
        color: 'var(--color-on-primary, #fff)',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      }}
    >
      <span style={{ fontSize: 14 }}>{status.message}</span>
      <Stack direction="horizontal" gap="2">
        <Button
          variant="secondary"
          data-testid="version-gate-soft-reload"
          onClick={reloadForUpdate}
        >
          Atualizar
        </Button>
        <Button
          variant="ghost"
          data-testid="version-gate-soft-dismiss"
          onClick={() => {
            rememberSoftDismiss(status.target_version)
            setDismissTick((n) => n + 1)
          }}
        >
          Depois
        </Button>
      </Stack>
    </div>
  )
}
